import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Grid from "@material-ui/core/Grid";
import Dialog from "@material-ui/core/Dialog";
import React from "react";
import Expression, {ExpressionJSON} from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Expression";
import {DistributionSummary} from "../Distributions/Distribution";
import Button from "@material-ui/core/Button";
import ProxyTypes, {
  DistributionDictionary,
  DistributionTimeDependence,
  ProxyTypeDisplayNames
} from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ProxyTypes";
import Card from "@material-ui/core/Card";
import {Theme, WithStyles} from "@material-ui/core";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import IconButton from "@material-ui/core/IconButton";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import Collapse from "@material-ui/core/Collapse";
import CardContent from "@material-ui/core/CardContent";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import DialogActions from "@material-ui/core/DialogActions";
import DistributionIcon from "@material-ui/icons/AccessibilityNew";
import ExponentialInput from "../Inputs/ExponentialInput";
import LogNormalInput from "../Inputs/LogNormalInput";
import WeibullInput from "../Inputs/WeibullInput";
import NormalInput from "../Inputs/NormalInput";
import DistributionSummaryPlot from "../Distributions/DistributionSummaryPlot";
import WarningIcon from "@material-ui/icons/Warning";
import yellow from "@material-ui/core/colors/yellow";
import InputRules from "../Inputs/InputRules";
import NonParametricInput from "../Inputs/NonParametricInput";

const styles = (theme: Theme) => createStyles({
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper
  },

  collapsed: {
    transform: "rotate(-90deg)"
  },

  scrollable: {
    height: "60vh",
    overflowY: "auto"
  },

  footer: {
    padding: "0 2rem"
  },

  title: {
    display: "flex",
    alignItems: "center"
  },

  icon: {
    // animation: `rotation 2s infinite linear`,
    marginRight: "1rem"
  },

  "@keyframes rotation": {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(359deg)',
    },
  },

  warning: {
    marginLeft: "1rem",
    color: yellow[500]
  }

});

interface Props extends WithStyles<typeof styles> {
  open: boolean;
  onClose: (e: React.SyntheticEvent<{}>) => void;
  onSelect: (e: React.MouseEvent<HTMLElement, MouseEvent>, selectedTrace: ExpressionJSON) => void;
}

interface State {
  traces: ExpressionJSON[];
  distributionSummaries: DistributionSummary[];
  errorDictionaries: {[key: string]: boolean}[];
  expanded: boolean[];
  selectedTraceIndex: number;
}

class DistributionPlaygroundDialog extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      traces: [{ _proxy: ProxyTypes.DISTRIBUTION }],
      expanded: [true],
      distributionSummaries: [null],
      errorDictionaries: [{ [ProxyTypes.DISTRIBUTION]: true }],
      selectedTraceIndex: 0
    };

  }

  private handleExpandClick = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, traceIndex: number) => {
    this.setState(prevState => ({
      expanded: [
        ...prevState.expanded.slice(0, traceIndex),
        !prevState.expanded[traceIndex],
        ...prevState.expanded.slice(traceIndex + 1)
      ]
    }));
  };

  private handleAddTraceClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    this.setState(prevState => ({
      traces: [...prevState.traces, { _proxy: ProxyTypes.DISTRIBUTION }],
      expanded: [...prevState.expanded.fill(false), true],
      distributionSummaries: [...prevState.distributionSummaries, null],
      errorDictionaries: [...prevState.errorDictionaries, { [ProxyTypes.DISTRIBUTION]: true }]
    }));
  };

  private handleDeleteTraceClick = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, traceIndex: number) => {
    this.setState(prevState => ({
      traces: [
        ...prevState.traces.slice(0, traceIndex),
        ...prevState.traces.slice(traceIndex + 1)
      ],
      expanded: [
        ...prevState.expanded.slice(0, traceIndex),
        ...prevState.expanded.slice(traceIndex + 1)
      ],
      distributionSummaries: [
        ...prevState.distributionSummaries.slice(0, traceIndex),
        ...prevState.distributionSummaries.slice(traceIndex + 1)
      ],
      errorDictionaries: [
        ...prevState.errorDictionaries.slice(0, traceIndex),
        ...prevState.errorDictionaries.slice(traceIndex + 1)
      ]
    }));
  };

  private handleTraceSelectClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const { onSelect, onClose } = this.props;
    const { traces, selectedTraceIndex } = this.state;
    onSelect(e, traces[selectedTraceIndex]);
    onClose(e);
  };

  private handleTraceSelectChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    this.setState({ selectedTraceIndex: Number(e.target.value) })
  };

  private handleTraceChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, error: boolean, traceIndex: number, multiple?: boolean) => {

    if(typeof e === 'string') {
      e = JSON.parse(e); // add `e` as a stringified object to have more control for expression output below
    } else {
      e.persist();
    }

    this.setState(prevState => {

      let updatedTrace = {
        ...prevState.traces[traceIndex],
        [e.target.name]: e.target.value
      };

      if (multiple) {
        updatedTrace = {
          ...prevState.traces[traceIndex],
          // @ts-ignore
          ...e.target.value
        };
      }

      return ({
        traces: [
          ...prevState.traces.slice(0, traceIndex),
          updatedTrace,
          ...prevState.traces.slice(traceIndex + 1)],

        distributionSummaries: [
          ...prevState.distributionSummaries.slice(0, traceIndex),
          new Expression(updatedTrace).toDistributionSummary(),
          ...prevState.distributionSummaries.slice(traceIndex + 1)
        ],

        errorDictionaries: [
          ...prevState.errorDictionaries.slice(0, traceIndex),
          { ...prevState.errorDictionaries[traceIndex], [prevState.traces[traceIndex]._proxy]: error },
          ...prevState.errorDictionaries.slice(traceIndex + 1)
        ]
      });

    });
  };

  private handleTimeDependenceChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, traceIndex: number) => {
    e.persist();

    const { traces } = this.state;
    if (traces[traceIndex].distribution_time_dependence === e.target.value) return;

    this.setState(prevState => {

      const updatedTrace = {
        ...prevState.traces[traceIndex],
        [e.target.name]: e.target.value,
        _proxy: ProxyTypes.DISTRIBUTION
      };

      return ({
        traces: [
          ...prevState.traces.slice(0, traceIndex),
          updatedTrace,
          ...prevState.traces.slice(traceIndex + 1)],

        distributionSummaries: [
          ...prevState.distributionSummaries.slice(0, traceIndex),
          null,
          ...prevState.distributionSummaries.slice(traceIndex + 1)
        ]
      });

    });
  };

  private handleDistributionTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, traceIndex: number) => {
    e.persist();

    const { traces } = this.state;
    if (traces[traceIndex]._proxy === e.target.value) return;

    this.setState(prevState => {

      const updatedTrace = {
        ...prevState.traces[traceIndex],
        [e.target.name]: e.target.value
      };

      const targetProxyErrorState = prevState.errorDictionaries[traceIndex][updatedTrace._proxy];
      const isTargetProxyInitialized = targetProxyErrorState !== null && targetProxyErrorState !== undefined;

      return ({
        traces: [
          ...prevState.traces.slice(0, traceIndex),
          updatedTrace,
          ...prevState.traces.slice(traceIndex + 1)],

        distributionSummaries: [
          ...prevState.distributionSummaries.slice(0, traceIndex),
          new Expression(updatedTrace).toDistributionSummary(),
          ...prevState.distributionSummaries.slice(traceIndex + 1)
        ],

        errorDictionaries: [
          ...prevState.errorDictionaries.slice(0, traceIndex),
          { ...prevState.errorDictionaries[traceIndex], [updatedTrace._proxy]: isTargetProxyInitialized ? targetProxyErrorState : true },
          ...prevState.errorDictionaries.slice(traceIndex + 1)
        ]

      });

    });

  };

  render() {

    const { classes, open, onClose } = this.props;
    const { traces, expanded, distributionSummaries, selectedTraceIndex, errorDictionaries } = this.state;

    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xl"
        disablePortal
      >
        <DialogTitle>
          <Grid
            container
            alignItems="center"
            justify="space-between"
          >
            <Grid className={classes.title} item>
              <DistributionIcon className={classes.icon} />
              <Typography variant="h5">Distribution Playground</Typography>
            </Grid>
            <Grid item>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Grid container spacing={10}>
              <Grid className={classes.scrollable} item xs={12} sm={12} md={4} lg={4} xl={4}>
                <Button
                  variant="outlined"
                  style={{ left: "50%", transform: "translateX(-50%)" }}
                  onClick={this.handleAddTraceClick}
                >
                  + trace to compare
                </Button>
                {
                  traces.map((trace, index) => (
                    <div style={{ width: "90%", margin: "1rem auto" }} key={Math.random()}>
                      <Card>
                        <div className={classes.cardHeader}>
                          <IconButton>
                            <ExpandMoreIcon
                              className={expanded[index] ? undefined : classes.collapsed}
                              onClick={e => this.handleExpandClick(e, index)}
                            />
                          </IconButton>
                          <div style={{ display: "flex" }}>
                            <Typography variant="body1">trace {index}</Typography>
                            {
                              errorDictionaries[index][trace._proxy] &&
                              <WarningIcon className={classes.warning} />
                            }
                          </div>
                          <IconButton>
                            <CloseIcon onClick={e => this.handleDeleteTraceClick(e, index)}/>
                          </IconButton>
                        </div>
                        <Collapse in={expanded[index]} timeout="auto" unmountOnExit>
                          <CardContent>
                            <TextField
                              label="Time Dependence"
                              name="distribution_time_dependence"
                              value={trace.distribution_time_dependence}
                              error={!trace.distribution_time_dependence}
                              helperText={trace.distribution_time_dependence ? undefined : InputRules.DESCRIPTIONS.EMPTY}
                              onChange={(e) => this.handleTimeDependenceChange(e, index)}
                              margin="normal"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              select
                              SelectProps={{
                                MenuProps: {
                                  disablePortal: true,
                                }
                              }}
                              variant="outlined"
                            >
                              {
                                Object.values(DistributionTimeDependence).map(distributionTimeDependence =>
                                  <MenuItem key={distributionTimeDependence} value={distributionTimeDependence}>
                                    {distributionTimeDependence}
                                  </MenuItem>
                                )
                              }
                            </TextField>
                            {
                              trace.distribution_time_dependence &&
                              <React.Fragment>
                                <TextField
                                  label="Type"
                                  name="_proxy"
                                  value={trace._proxy}
                                  onChange={e => this.handleDistributionTypeChange(e, index)}
                                  error={trace._proxy === ProxyTypes.DISTRIBUTION}
                                  helperText={trace._proxy === ProxyTypes.DISTRIBUTION ? InputRules.DESCRIPTIONS.EMPTY : undefined}
                                  margin="normal"
                                  fullWidth
                                  select
                                  SelectProps={{
                                    MenuProps: {
                                      disablePortal: true,
                                    }
                                  }}
                                  variant="outlined"
                                >
                                  {
                                    DistributionDictionary[trace.distribution_time_dependence].map(proxyType =>
                                      <MenuItem key={proxyType} value={proxyType}>
                                        {ProxyTypeDisplayNames[proxyType]}
                                      </MenuItem>
                                    )
                                  }
                                </TextField>
                                {
                                  DistributionDictionary[trace.distribution_time_dependence].includes(trace._proxy) &&
                                  <React.Fragment>
                                    {
                                      trace._proxy === ProxyTypes.EXPONENTIAL_DISTRIBUTION &&
                                      <ExponentialInput
                                       testInterval={trace.exponential_test_interval}
                                       failureRate={trace.failure_rate}
                                       onChange={(e, error) => this.handleTraceChange(e, error, index)}
                                      />
                                    }
                                    {
                                      trace._proxy === ProxyTypes.LOG_NORMAL_DISTRIBUTION &&
                                      <LogNormalInput
                                        logNormalParams={trace.log_normal_params}
                                        logNormalMean={trace.log_normal_mean}
                                        logNormalStd={trace.log_normal_std}
                                        logNormalMedian={trace.log_normal_median}
                                        logNormalErrorFactor={trace.log_normal_error_factor}
                                        logNormalP5={trace.log_normal_p5}
                                        logNormalP95={trace.log_normal_p95}
                                        onChange={(e, error) => this.handleTraceChange(e, error, index)}
                                      />
                                    }
                                    {
                                      trace._proxy === ProxyTypes.WEIBULL_DISTRIBUTION &&
                                      <WeibullInput
                                        testInterval={trace.weibull_test_interval}
                                        shape={trace.shape}
                                        scale={trace.scale}
                                        onChange={(e, error) => this.handleTraceChange(e, error, index)}
                                      />
                                    }
                                    {
                                      trace._proxy === ProxyTypes.NORMAL_DISTRIBUTION &&
                                      <NormalInput
                                        normalParams={trace.normal_params}
                                        normalMean={trace.normal_mean}
                                        normalStd={trace.normal_std}
                                        normalMedian={trace.normal_median}
                                        normalErrorFactor={trace.normal_error_factor}
                                        normalP5={trace.normal_p5}
                                        normalP95={trace.normal_p95}
                                        onChange={(e, error) => this.handleTraceChange(e, error, index)}
                                      />
                                    }
                                    {
                                      trace._proxy === ProxyTypes.NON_PARAMETRIC_DISTRIBUTION &&
                                      <NonParametricInput
                                        timeToFailure={trace.time_to_failure}
                                        estimatedReliability={trace.estimated_reliability}
                                        onChange={(e, error, multiple) => this.handleTraceChange(e, error, index, multiple)}
                                      />
                                    }
                                  </React.Fragment>
                                }
                              </React.Fragment>
                            }
                          </CardContent>
                        </Collapse>
                      </Card>
                    </div>
                  ))
                }
              </Grid>
              <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                <DistributionSummaryPlot
                  distributionSummaries={distributionSummaries}
                />
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.footer}>
          <TextField
            label="trace"
            name="trace"
            value={selectedTraceIndex}
            onChange={this.handleTraceSelectChange}
            margin="normal"
            fullWidth
            select
            SelectProps={{
              MenuProps: {
                disablePortal: true,
              }
            }}
            disabled={traces.length === 0}
            variant="filled"
          >
            {
              traces.map((trace, index) =>
                <MenuItem key={Math.random()} value={index}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {`trace ${index}`}
                    {
                      errorDictionaries[index][trace._proxy] &&
                      <WarningIcon className={classes.warning} />
                    }
                  </div>
                </MenuItem>
              )
            }
          </TextField>
          <Button
            variant="outlined"
            disabled={traces.length <= selectedTraceIndex || errorDictionaries[selectedTraceIndex][traces[selectedTraceIndex]._proxy]}
            onClick={this.handleTraceSelectClick}
          >
            Select
          </Button>
        </DialogActions>
      </Dialog>
    );
  }


}

export default withStyles(styles)(DistributionPlaygroundDialog);
